import { ReactNode } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { Settings, MapPin, Grid3x3, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface POSAdminLayoutProps {
  children: ReactNode;
}

const navItems = [
  { to: '/apps/pos/admin/settings', icon: Settings, label: 'Settings' },
  { to: '/apps/pos/admin/areas', icon: MapPin, label: 'Areas' },
  { to: '/apps/pos/admin/tables', icon: Grid3x3, label: 'Tables' },
];

export const POSAdminLayout = ({ children }: POSAdminLayoutProps) => {
  const location = useLocation();

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <div className="w-64 bg-card border-r border-border shadow-sm">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <Link to="/apps/pos">
            <Button variant="ghost" size="sm" className="gap-2 w-full justify-start">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to POS</span>
            </Button>
          </Link>
        </div>

        {/* Title */}
        <div className="p-4">
          <h1 className="text-lg font-bold text-foreground">POS Admin</h1>
          <p className="text-sm text-muted-foreground">Configuration</p>
        </div>

        {/* Navigation */}
        <nav className="px-2 py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;

            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                  isActive
                    ? 'bg-emerald-50 text-emerald-700 font-medium'
                    : 'text-foreground hover:bg-accent'
                )}
              >
                <Icon className={cn('w-5 h-5', isActive && 'text-emerald-600')} />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};
